/**
 * Cross-app login redirect helpers.
 *
 * ZAFROI has a single canonical sign-in URL — `https://zafroi.com/login`.
 * The ERP bundle is served at three origins (zafroi.com, www.zafroi.com,
 * erp.zafroi.com); the engage bundle at engage.zafroi.com. ANY of those
 * apps, when it sees an unauthenticated user, must bounce to the single
 * canonical login origin instead of rendering /login on a subdomain.
 *
 * Override the canonical URL via `VITE_PLATFORM_LOGIN_URL` for local dev.
 */

const CANONICAL_LOGIN_URL =
  (import.meta.env.VITE_PLATFORM_LOGIN_URL as string | undefined) ??
  'https://zafroi.com/login';

export function getPlatformLoginUrl(opts: { from?: string } = {}): string {
  const url = new URL(CANONICAL_LOGIN_URL);
  const from =
    opts.from ?? (typeof window !== 'undefined' ? window.location.href : '');
  if (from) url.searchParams.set('from', from);
  return url.toString();
}

/**
 * Absolute URL on the canonical auth origin for a given path (+search). Used to
 * forward the ERP's now-removed public auth routes (/login, /login/reset?token=…,
 * /login/invite?token=…, etc.) to the platform app that owns them — preserving
 * the path and any token query so reset/invite links keep working.
 */
export function canonicalAuthUrl(pathAndSearch: string): string {
  const origin = new URL(CANONICAL_LOGIN_URL).origin;
  return origin + pathAndSearch;
}

/**
 * Whether THIS bundle renders the canonical login. Per ADR-004 the ERP never
 * does anymore — the platform app serves `zafroi.com/login` even at the apex
 * (nginx routes the `/login*` path to the platform build), so the ERP's own
 * `/login` is dead. Returning false here makes every call site — logout,
 * RequireAuth, AuthRouter, the ERP LoginPage's self-bounce — full-page redirect
 * to the canonical platform login instead of ever showing the old ERP form.
 *
 * (Kept as a function, not a constant, so the redirect call sites stay
 * unchanged and the ERP's legacy auth pages can be removed in a follow-up.)
 */
export function isOnCanonicalLoginOrigin(): boolean {
  return false;
}

/**
 * Hard-redirect to the canonical login. Uses `location.replace` so the
 * current history entry is replaced — back button won't return the user
 * to the (now-protected) page that triggered the bounce. Pass `from` to
 * explicitly override the bounce-back target; defaults to current href.
 */
export function redirectToPlatformLogin(opts: { from?: string } = {}): void {
  if (typeof window === 'undefined') return;
  window.location.replace(getPlatformLoginUrl(opts));
}

/**
 * Open-redirect protection. Only honor `?from=` URLs that point at a
 * zafroi.com domain (apex or any subdomain) or at localhost for dev.
 * Anything else falls back to the default landing.
 */
export function isAllowedReturnTarget(rawUrl: string): boolean {
  if (!rawUrl) return false;
  try {
    const u = new URL(rawUrl);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return false;
    const host = u.hostname.toLowerCase();
    if (host === 'localhost' || host === '127.0.0.1') return true;
    return host === 'zafroi.com' || host.endsWith('.zafroi.com');
  } catch {
    return false;
  }
}
